import { useEffect, useRef, useState, useCallback } from "react";
import axios from "axios";
import { Loader2, ShieldCheck, X } from "lucide-react";
import { Button } from "./ui/button";
import { MobileDialog } from "./mobile";
import { formatApiError } from "../utils/apiError";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * SudoGate — global axios response interceptor that handles the
 * `sudo_required` step-up auth flow for sensitive actions.
 *
 * Flow:
 *  1. Sensitive endpoint returns HTTP 403 `{ detail: "sudo_required" }`
 *  2. We intercept, open a password re-entry modal
 *  3. User enters password → POST /api/auth/sudo issues short-lived cookie
 *  4. Original request is replayed automatically — the caller gets the
 *     successful response as if sudo was never needed.
 *
 * Drop <SudoGate /> once anywhere inside <AuthProvider> — no other wiring.
 */
export default function SudoGate() {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const pendingRef = useRef(null); // { config, resolve, reject }

  const closeAndReject = useCallback(() => {
    const p = pendingRef.current;
    pendingRef.current = null;
    setOpen(false);
    setPassword("");
    setError("");
    if (p) p.reject({ __sudo_cancelled__: true });
  }, []);

  const submit = useCallback(async (e) => {
    e?.preventDefault?.();
    if (submitting || !pendingRef.current) return;
    setSubmitting(true);
    setError("");
    try {
      await axios.post(
        `${API}/api/auth/sudo`,
        { password },
        { withCredentials: true }
      );
      // Replay the original request
      const { config, resolve, reject } = pendingRef.current;
      pendingRef.current = null;
      setOpen(false);
      setPassword("");
      try {
        const replay = await axios.request({
          ...config,
          withCredentials: true,
          headers: { ...(config.headers || {}), "X-Sudo-Retry": "1" },
        });
        resolve(replay);
      } catch (err) {
        reject(err);
      }
    } catch (err) {
      setError(formatApiError(err, "Incorrect password"));
    } finally {
      setSubmitting(false);
    }
  }, [password, submitting]);

  useEffect(() => {
    const id = axios.interceptors.response.use(
      (r) => r,
      (err) => {
        const status = err?.response?.status;
        const detail = err?.response?.data?.detail;
        const alreadyRetried = err?.config?.headers?.["X-Sudo-Retry"] === "1";
        if (status === 403 && detail === "sudo_required" && !alreadyRetried) {
          return new Promise((resolve, reject) => {
            pendingRef.current = { config: err.config, resolve, reject };
            setOpen(true);
          });
        }
        return Promise.reject(err);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, []);

  if (!open) return null;

  return (
    <MobileDialog
      bareContent
      open={open}
      onClose={closeAndReject}
      testid="sudo-modal"
      priority="urgent"
      presentation="center"
    >
      <div className="w-full max-w-sm bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-700 p-6 mx-4">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-slate-900 dark:bg-white flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-white dark:text-slate-900" />
            </div>
            <div>
              <p className="font-bold text-slate-900 dark:text-white text-sm">Confirm it's you</p>
              <p className="text-[11px] text-slate-500 dark:text-slate-400">For this sensitive action</p>
            </div>
          </div>
          <button
            data-testid="sudo-close-btn"
            onClick={closeAndReject}
            className="text-slate-400 hover:text-slate-600"
            aria-label="Cancel"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-3">
          <input
            data-testid="sudo-password-input"
            type="password"
            autoFocus
            autoComplete="current-password"
            placeholder="Your password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full h-11 rounded-xl border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-4 text-sm text-slate-900 dark:text-white focus:outline-none focus:border-slate-900 dark:focus:border-white"
          />
          {error && (
            <p data-testid="sudo-error" className="text-xs text-red-600">{error}</p>
          )}
          <Button
            data-testid="sudo-confirm-btn"
            type="submit"
            disabled={!password || submitting}
            className="w-full h-11 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-sm"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm"}
          </Button>
          <p className="text-[11px] text-slate-400 text-center pt-1">
            Valid for 5 minutes on this device
          </p>
        </form>
      </div>
    </MobileDialog>
  );
}
